import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";
import { VenueForm } from "Venues/components";
import { createVenue, dataMetaSelector } from "Venues/state";
import { IVenuePostData, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

import { VenueExtraForm } from "Bands/components";
import { useBandContext, useVenueBlockContext } from "Bands/contexts";

const { venueBlock: venueBlockCopy } = copy;

const buttons: IFormButtons = {
  primary: {
    children: venueBlockCopy.buttons.useVenue,
    size: "m",
    "data-testid": "create_venue_button",
  },
};

export const CreateVenue: React.FC = () => {
  const { setVenueId } = useVenueBlockContext();
  const { bandId, userBands } = useBandContext();
  const { api: apiMeta, id: venueId } = useSelector(dataMetaSelector(VENUE_ACTIONS.createVenue));
  useClearActionMetaOnUnMount(VENUE_ACTIONS.createVenue);

  const dispatch = useDispatch();

  // etrxa fields
  const [bands, setBands] = useState<string[] | null>(null);
  const [notify, setNotify] = useState<boolean>(false);
  const [users, setUsers] = useState<string[] | null>(null);

  const handleSubmit = (venue: IVenuePostData) => {
    const cleanVenue: IVenuePostData = {
      ...venue,
      bands,
      notify,
    };

    dispatch(createVenue({ venue: cleanVenue, users: users || [] }));
  };

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && venueId) {
      setVenueId(venueId);

      dispatch(
        addTemporaryToast({
          message: copy.createVenue.success,
          status: IStatus.success,
        })
      );
    }
  }, [apiMeta, dispatch, venueId, setVenueId]);

  const extraFormDefaultValues = useMemo(
    () => ({
      bands: [bandId],
    }),
    [bandId]
  );

  if (!bandId) return null;

  return (
    <Card theme={ICardTheme.secondary} data-testid="create_venue_form">
      <Spacer size="l">
        <Title asComponent="h3" size="m">
          {venueBlockCopy.createVenue.title}
        </Title>
        <VenueForm
          showExtendedInfo={false}
          apiStatus={apiMeta?.status}
          hasError={!!apiMeta?.error}
          buttons={buttons}
          handleSubmit={handleSubmit}
        >
          <VenueExtraForm
            currentBandId={bandId}
            bands={userBands}
            defaultValues={extraFormDefaultValues}
            handleBandsChange={setBands}
            handleNotifyChange={setNotify}
            handleUsersChange={setUsers}
          />
        </VenueForm>
      </Spacer>
    </Card>
  );
};
